import AddIcon from '@mui/icons-material/Add';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { extractErrorMessage } from '~/utils/error/error';
import api from '~/services/api';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import Table from '~/ui/pages/Clients/reusable/Table';
import { ClientAction, IClientAction } from '~/ui/pages/Clients/reusable/Table/types';
import Button from '~/ui/components/common/Button';
import { ADD_CLIENT } from '~/ui/constants/paths';
import ModalForm from './ModalForm';
import RestoreModal from './RestoreModal';

import styles from './ClientsManagement.module.scss';

const ClientsManagements = (): ReactElement => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [clientAction, setClientAction] = useState<IClientAction | null>(null);
  const [dictionaries, setDictionaries] = useState<{ id: number; value: string }[]>([]);

  const clinicId = useStoreState(state => state.clinic.current?.id);
  const { clinic } = useStoreState(state => state.user.current);

  const { list: clients } = useStoreState(state => state.client);

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const onGetClinicClients = useStoreActions(actions => actions.client.onGetClinicClients);
  const { onGetActTeamsAvailable } = useStoreActions(action => action.actTeam);
  const onRestoreClient = useStoreActions(actions => actions.client.onRestoreClient);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const filters = useStoreState(state => state.client.filters);
  const pagination = useStoreState(state => state.client.pagination);

  const { isGlobalUser } = useRole();

  const refetchClients = async () => {
    const { teamIds, ...restFilters } = filters;

    await onGetClinicClients({
      clinicId: String(clinicId),
      teamIds: teamIds.map(String),
      params: { pageSize: pagination.pageSize, pageNumber: pagination.pageNumber, ...restFilters },
    });
  };

  // fetch available teams for multiselect and dictionaries on mount
  const onMount = useCallback(async () => {
    setLoading(true);
    try {
      if (!clinicId) {
        await onGetMyClinic();
      }

      if (clinicId) {
        await onGetActTeamsAvailable({ clinicId: String(clinicId) });
      }

      if (!dictionaries?.length) {
        const { data } = await api.dictionaries.getAvailableTypeList(
          IDictionaryTypes.DischargeReason,
        );
        setDictionaries(data);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [clinicId, dictionaries?.length, onGetActTeamsAvailable, onGetMyClinic, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const options = dictionaries?.map(({ id, value }) => ({ label: value, value: id }));

  const onConfirmRestore = async (teamId: string) => {
    try {
      await onRestoreClient({
        clinicId: String(clinicId),
        teamId,
        id: String(clientAction?.client.teams.find(t => t.team.id === Number(teamId))?.id || ''),
        clientId: String(clientAction?.client.id),
      });
      showNotify({ message: 'Client successfully restored' });
      setClientAction(null);
      refetchClients();
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const actions = {
    [ClientAction.ARCHIVE]: (
      <ModalForm
        client={clientAction?.client}
        clinicId={clinicId}
        setClientAction={setClientAction}
        options={options}
        refetch={refetchClients}
      />
    ),
    [ClientAction.RESTORE]: (
      <RestoreModal
        client={clientAction?.client}
        onConfirm={onConfirmRestore}
        onClose={() => setClientAction(null)}
      />
    ),
  };

  return (
    <div>
      <div className={styles.header}>
        <h2>Clients</h2>
        {!isGlobalUser && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(ADD_CLIENT)}
          >
            Add Client
          </Button>
        )}
      </div>
      <Table
        showTeamSelect
        list={clients}
        onGetClients={onGetClinicClients}
        clinicId={clinicId || clinic?.id}
        loading={loading}
        setClientAction={setClientAction}
        withActions
      />
      {!isGlobalUser && actions[clientAction?.action]}
    </div>
  );
};

export default ClientsManagements;
